<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import {
  PDropdown,
  IconFilterSortOffSm as FilterIcon,
  PButton,
} from "@profeat/ui-kit";

import StringFilter from "~/components/customers/v2/table/filter/CustomersStringFilter.vue";
import NumberFilter from "~/components/customers/v2/table/filter/CustomersNumberFilter.vue";
import TypeFilter from "~/components/customers/v2/table/filter/CustomersTypeFilter.vue";
import TagFilter from "~/components/customers/v2/table/filter/CustomersTagFilter.vue";
import DateFilter from "~/components/customers/v2/table/filter/CustomersDateFilter.vue";
import BirthdayFilter from "~/components/customers/v2/table/filter/CustomersBirthdayFilter.vue";
import {
  ContactField,
  CustomFieldType,
  StaticFieldType,
  SystemFieldType,
  TagField,
} from "~/entities/contacts";
import { useContacts } from "~/stores/contacts.store";

const filterComponents = {
  [SystemFieldType.Phone]: StringFilter,
  [SystemFieldType.Date]: DateFilter,
  [SystemFieldType.Enum]: TypeFilter,
  [SystemFieldType.Number]: NumberFilter,
  [SystemFieldType.String]: StringFilter,
  [SystemFieldType.Birthday]: BirthdayFilter,
  [CustomFieldType.Text]: StringFilter,
  [CustomFieldType.Int]: NumberFilter,
  [StaticFieldType.Tag]: TagFilter,
};

const props = defineProps<{
  field: ContactField | TagField;
}>();

const contactsStore = useContacts();

const dropDown = ref(false);

const changed = ref(false);
const elWrap = ref();
const filterValue = ref<unknown>(null);

const filterWidth = computed(() => {
  return [SystemFieldType.Enum, StaticFieldType.Tag].includes(props.field.type)
    ? 180
    : props.field.type === SystemFieldType.Date
    ? 400
    : 328;
});
const filtered = computed<boolean>(() => {
  if (props.field.name === "status") {
    const filter = contactsStore.filterBuilder.getFilterById(props.field.id);

    return (
      Array.isArray(filter) &&
      filter.some((item) => ["lost", "archive"].includes(item))
    );
  }

  return !!contactsStore.filterBuilder.getFilterById(props.field.id);
});

const applyFilter = async (visible = false, withoutCall = false) => {
  dropDown.value = visible;

  if (withoutCall) return;

  await contactsStore.fetchMetaContacts(false);
  contactsStore.isAllSelected = true;
  contactsStore.selectionMode = "all";
};
const resetFilter = async (visible = false, withoutCall = false) => {
  dropDown.value = false;
  changed.value = false;
  filterValue.value = null;

  contactsStore.deleteFilterValue(props.field.id);
  contactsStore.filterBuilder.remove(props.field.id);
  contactsStore.isAllSelected = true;
  contactsStore.selectionMode = "all";

  if (withoutCall) return;
  if (props.field.name === "status") {
    contactsStore.filterBuilder.type("status", props.field.id, [
      "active",
      "new",
    ]);
  }
  await contactsStore.fetchMetaContacts(false);
};
const setFilterValue = (payload: unknown) => {
  contactsStore.setFilterValues(
    payload as { [fieldId: string]: unknown },
    props.field.id
  );
  filterValue.value = payload;
};
onMounted(() => {
  const values = { ...contactsStore.filterValues };
  if (values[props.field.id]) {
    filterValue.value = values[props.field.id];
  }
});
</script>

<template>
  <div class="filter">
    <PDropdown
      v-model="dropDown"
      :width="filterWidth"
      :close-on-self-click="false"
      :ignore-click-outside="true"
    >
      <template #activator="{ setReference, click }">
        <div ref="elBtn">
          <div
            :ref="setReference"
            :class="[
              'icon',
              { active: field.id === 'status' || filtered || changed },
            ]"
            @click="click"
          >
            <FilterIcon />
          </div>
        </div>
      </template>

      <template #default>
        <div class="content" ref="elWrap">
          <component
            :is="filterComponents[field.type]"
            :field="field"
            :filter-builder="contactsStore.filterBuilder"
            :refWrap="elWrap"
            :filter-value="filterValue"
            @apply-filter="applyFilter"
            @reset-filter="resetFilter"
            @click-outside="() => (dropDown = false)"
            @set-filter-value="setFilterValue"
            v-model:changed="changed"
          />
          <PButton
            v-show="filtered || changed"
            size="x-small"
            color="secondary"
            @click="resetFilter"
          >
            {{ $t("customers.filter.reset") }}
          </PButton>
        </div>
      </template>
    </PDropdown>
  </div>
</template>

<style lang="scss" scoped>
.filter {
  position: relative;

  :deep(.dropdown-content) {
    cursor: default;
  }
  .icon {
    cursor: pointer;
    display: flex;
  }

  .content {
    display: flex;
    flex-direction: column;
    grid-gap: 16px;
    user-select: text;
  }

  .active::before {
    content: "";
    display: inline-block;
    position: absolute;
    width: 10px;
    height: 10px;
    left: 8px;
    border: 2px solid $ui-white;
    border-radius: 7.5px;
    background-color: #69b6d5;
  }
}
</style>
